import { Close, Done, InfoOutlined } from "@mui/icons-material";
import {
    Autocomplete,
    Checkbox,
    FormControl,
    IconButton,
    TableCell,
    TextField,
    Typography,
} from "@mui/material";
import {
    ChangeEvent,
    Dispatch,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useDebounce } from "../../../Hooks/useDebounce";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import { CollectBillsCtx } from "./CollectBillsCtx";

type Props = {
    collectee: Collectee;
    // collections: { [key: number]: RawCollection };
    // setCollections: Dispatch<SetStateAction<{ [key: number]: RawCollection }>>;
    // collectionsRef: MutableRefObject<{ [key: number]: RawCollection }>;
    // collectionDate: string;
    // paymentMethodRes: any[];
    // collectionResults: any;
    setActiveResult: Dispatch<SetStateAction<any>>;
    count: number;
    // checkAll: boolean;
    rowIndex: number;
    // amountInputsRef: MutableRefObject<{ [rowIndex: number]: HTMLInputElement }>;
    // adjustmentInputsRef: MutableRefObject<{
    //     [rowIndex: number]: HTMLInputElement;
    // }>;
    // columnsVisibility: { [key: string]: boolean };
    // defaultPaymentMethod: null | { id: string; name: string };
    // checkedRows: { [key: number]: boolean };
    // setCheckedRows: Dispatch<SetStateAction<{ [key: number]: boolean }>>;
};

const calculateSurcharge = (
    collectee: Collectee,
    collectionDate: string
): number => {
    let surchargeAmount = 0;
    const _collDate = new Date(collectionDate).getTime();

    for (const reading of collectee.readings) {
        const dueDate = new Date(reading.dueDate).getTime();

        if (dueDate < _collDate) {
            surchargeAmount += reading.applicableSurcharge;
        }
    }

    return surchargeAmount;
};

const formatter = Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
});

const initialRowData: RawCollection = {
    amount: 0,
    adjustment: 0,
    advance: 0,
    description: "",
    paymentMethod: null,
    checked: false,
    meterNo: "",
};

export const CollectionRow = ({
    collectee,
    setActiveResult,
    count,
    rowIndex,
}: Props) => {
    const [rowData, setRowData] = useState(initialRowData);
    const [isReady, setIsReady] = useState(true);
    const {
        collectionDate,
        collectionsRef,
        columnsVisibility,
        paymentMethodsRes,
        collectionResults,
        defaultPaymentMethod,
    } = useContext(CollectBillsCtx);
    const [errorAmountMessage, setErrorAmountMessage] = useState("");
    const [errorAdjustmentMessage, setErrorAdjustmentMessage] = useState("");
    const [errorAdvanceMessage, setErrorAdvanceMessage] = useState("");

    const acsPaid = useMemo(() => {
        const acs = collectee.readings
            .map((r) => r.additionalCharges.map((ac: any) => ac.collections))
            .flat(2);
        return acs.reduce(
            (prev, curr) => prev + curr.amount + curr.adjustment,
            0
        );
    }, [collectionDate, collectee.id]);

    const handleChange = (ev: ChangeEvent<any>, id: number) => {
        const { name, value } = ev.target;

        let amount = rowData.amount || 0;
        let adjustment = rowData.adjustment || 0;
        let advance = 0; // IMPORTANT: reset every time

        const numericValue = parseFloat(value) || 0;

        if (name === "amount") amount = numericValue;
        if (name === "adjustment") adjustment = numericValue;

        const paid = amount + adjustment;

        // Pure derivation
        if (paid > totalAmount) {
            advance = +(paid - totalAmount).toFixed(2);
        }

        const updatedRow = {
            ...rowData,
            amount,
            adjustment,
            advance,
            checked: true,
        };

        setRowData(updatedRow);

        collectionsRef.current = {
            ...collectionsRef.current,
            [id]: updatedRow,
        };
    };

    const handleSelect = (ev: any, checked: boolean) => {
        setRowData((curr) => {
            const updated = { ...curr, checked: checked };
            collectionsRef.current[collectee.id] = updated;
            return updated;
        });
    };

    const surcharge = useMemo(() => {
        let appliedSurcharge = 0;
        let paidSurcharge = 0;
        let surchargeAdj = 0;

        for (const reading of collectee.readings) {
            const dueTime = new Date(reading.dueDate).getTime();

            // Check if ANY collection is within due date
            const hasCollectionWithinDueDate = reading.collections.some(
                (collection: any) =>
                    new Date(collection.collectionDate).getTime() <= dueTime
            );

            // If even one collection is within due date → NO surcharge
            if (hasCollectionWithinDueDate) {
                continue;
            }

            // If no collection exists, use current collectionDate
            const effectiveCollectionDate =
                reading.collections.length > 0
                    ? Math.min(
                          ...reading.collections.map((c: any) =>
                              new Date(c.collectionDate).getTime()
                          )
                      )
                    : new Date(collectionDate).getTime();

            // Apply surcharge only if late
            if (effectiveCollectionDate > dueTime) {
                appliedSurcharge += reading.applicableSurcharge;
                paidSurcharge += reading.payedSurcharge;
                surchargeAdj += reading.surchargeAdj;
            }
        }

        return appliedSurcharge - (paidSurcharge + surchargeAdj);
    }, [collectionDate, collectee.id]);

    const totalAmount = useMemo(() => {
        const readingTotal = collectee.readings.reduce(
            (prev, curr) => prev + curr.totalAmount,
            0
        );

        const collectionTotal = collectee.readings.reduce(
            (prev, curr) =>
                prev +
                curr.collections.reduce(
                    (p: any, c: any) => p + c.amount + c.adjustment,
                    0
                ),
            0
        );
        console.log(collectionTotal, "collectionTotal");

        console.log(surcharge);

        const total =
            readingTotal +
            // parseInt(acsTotal as any) +
            surcharge -
            collectionTotal -
            acsPaid;

        return total;
    }, [surcharge, collectee.id, acsPaid]);

    useEffect(() => {
        if (!rowData.checked) return;

        setRowData((curr) => ({
            ...curr,
            paymentMethod: curr.paymentMethod
                ? curr.paymentMethod
                : defaultPaymentMethod
                  ? (defaultPaymentMethod as any)
                  : null,
        }));
    }, [rowData.checked, defaultPaymentMethod?.id]);

    useEffect(() => {
        // const fractional = +(totalAmount - Math.floor(totalAmount)).toFixed(2);
        setRowData((curr) => ({
            ...curr,
            checked: collectionsRef?.current[collectee.id]?.checked,
            meterNo: collectionsRef.current[collectee.id]?.meterNo,
            // adjustment: fractional,
        }));
    }, [collectionsRef?.current[collectee.id]?.checked]);

    useEffect(() => {
        setRowData((curr) => ({
            ...curr,
            ...collectionsRef.current[collectee.id],
        }));
    }, []);

    return (
        <>
            <TableCell>
                <FormControl>
                    <Checkbox
                        size="small"
                        checked={rowData.checked}
                        onChange={handleSelect}
                    />
                </FormControl>
            </TableCell>

            {columnsVisibility["Response"] &&
            collectionResults[collectee.id] ? (
                <TableCell>
                    <IconButton onClick={() => setActiveResult(collectee.id)}>
                        {collectionResults[collectee.id].success ? (
                            <Done color="success" />
                        ) : (
                            <Close color="error" />
                        )}
                    </IconButton>
                </TableCell>
            ) : (
                columnsVisibility["Response"] && (
                    <TableCell>
                        <IconButton disabled>
                            <InfoOutlined color="disabled" />
                        </IconButton>
                    </TableCell>
                )
            )}

            {columnsVisibility["Serial no."] && <TableCell>{count}</TableCell>}
            {columnsVisibility["Meter no."] && (
                <TableCell> {collectee?.meterNo}</TableCell>
            )}

            {columnsVisibility["Customer"] && (
                <TableCell> {collectee?.property?.customer?.name}</TableCell>
            )}

            {columnsVisibility["Property name"] && (
                <TableCell>{collectee?.property?.name}</TableCell>
            )}
            {columnsVisibility["Total"] && (
                <TableCell>{totalAmount.toFixed(4)}</TableCell>
            )}

            <AmountCell
                rowIndex={rowIndex}
                totalAmount={+totalAmount.toString().split(".")[0]}
                collectee={collectee}
                onChange={handleChange}
                isReady={isReady}
                defaultAmount={+rowData.amount.toString().split(".")[0]}
                errorMessage={errorAmountMessage}
            />

            <AdjustmentCell
                key={`adjustment-${rowIndex}`}
                collectee={collectee}
                rowIndex={rowIndex}
                onChange={handleChange}
                isReady={isReady}
                defaultAdjustment={rowData.adjustment}
                errorAdjustmentMessage={errorAdjustmentMessage}
            />
            <AdvanceCell
                key={`advance-${rowIndex}`}
                collectee={collectee}
                rowIndex={rowIndex}
                onChange={handleChange}
                isReady={isReady}
                defaultAdvance={rowData.advance}
                errorAdvanceMessage={errorAdvanceMessage}
            />

            {columnsVisibility["Remaining"] && (
                <TableCell>
                    {columnsVisibility["Remaining"] && (
                        <TableCell>
                            <Typography>
                                {Math.max(
                                    0,
                                    totalAmount -
                                        (rowData.amount + rowData.adjustment)
                                ).toFixed(2)}
                            </Typography>
                        </TableCell>
                    )}
                </TableCell>
            )}

            {columnsVisibility["Payment Method"] && (
                <TableCell style={{ minWidth: "256px" }}>
                    <Autocomplete
                        key={`payment-method-${collectee.id}-${rowData.paymentMethod?.id}`}
                        fullWidth
                        size="small"
                        options={paymentMethodsRes}
                        getOptionLabel={(option: any) => option.name}
                        value={rowData.paymentMethod || null}
                        onChange={(_, newValue: any) => {
                            setRowData((curr) => {
                                const change = {
                                    ...curr,
                                    paymentMethod: newValue,
                                };

                                collectionsRef.current = {
                                    ...collectionsRef.current,
                                    [collectee.id]: {
                                        ...collectionsRef.current[collectee.id],
                                        ...change,
                                    },
                                };

                                return change;
                            });
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Payment Method"
                                required={
                                    rowData.checked == true &&
                                    !rowData?.paymentMethod?.id
                                }
                            />
                        )}
                    />
                </TableCell>
            )}

            <DescriptionCell
                collectee={collectee}
                onChange={handleChange}
                rowIndex={rowIndex}
                isReady={isReady}
                defaultDescription={rowData.description}
            />
        </>
    );
};

type CommonCellProps = {
    rowIndex: number;
    onChange: (ev: ChangeEvent<any>, id: number) => void;
    collectee: Collectee;
    isReady: boolean;
};

function AmountCell({
    rowIndex,
    totalAmount,
    onChange,
    collectee,
    isReady,
    defaultAmount,
    errorMessage,
}: CommonCellProps & {
    totalAmount: number;
    defaultAmount?: number;
    errorMessage: string | null;
}) {
    const [amount, setAmount] = useState(0);
    const { amountInputsRef, columnsVisibility } = useContext(CollectBillsCtx);

    function handleChange(ev: ChangeEvent<any>, id: number) {
        const { value } = ev.target;
        const validAmount = Math.min(totalAmount, parseFloat(value) || 0);
        setAmount(validAmount);

        onChange(ev, id);
    }

    useEffect(() => {
        setAmount(defaultAmount || 0);
    }, [defaultAmount]);

    if (!columnsVisibility["Collect amount"]) return null;

    if (!isReady) return <TableCell />;

    return (
        <TableCell>
            <input
                // autoFocus={rowIndex === 0}
                ref={(el) => {
                    if (el) {
                        amountInputsRef.current[rowIndex] = el;
                    }
                }}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        if (amountInputsRef.current[rowIndex + 1]) {
                            e.preventDefault();
                            amountInputsRef.current[rowIndex + 1].focus();
                        }
                    }
                }}
                // size="small"
                name="amount"
                // label="Amount"
                placeholder="Amount"
                value={amount || ""}
                onChange={(ev) => handleChange(ev, collectee.id)}
                type="number"
                min={0}
                step={0.01}
                style={{
                    padding: "0.65rem",
                    width: "100%",
                    minWidth: "15ch",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                }}
                // inputProps={{ min: 0, max: totalAmount }}
                // sx={{
                //     width: "20ch",
                //     "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                //         {
                //             display: "none",
                //         },
                //     "& input[type=number]": {
                //         MozAppearance: "textfield", // Removes spinner in Firefox
                //     },
                //     minWidth: "20ch",
                // }}
                // InputProps={{
                //     inputProps: {
                //         style: {
                //             appearance: "textfield", // Removes spinner in modern browsers
                //         },
                //     },
                // }}
            />

            {errorMessage && (
                <span style={{ color: "red", fontSize: "0.8rem" }}>
                    {errorMessage}
                </span>
            )}
        </TableCell>
    );
}

function DescriptionCell({
    collectee,
    onChange,
    rowIndex,
    isReady,
    defaultDescription,
}: CommonCellProps & { defaultDescription?: string }) {
    const [description, setDescription] = useState("");
    const { columnsVisibility } = useContext(CollectBillsCtx);

    function handleChange(ev: ChangeEvent<any>, id: number) {
        const { value } = ev.target;
        setDescription(value);
        onChange(ev, id);
    }

    useEffect(() => {
        setDescription(defaultDescription || "");
    }, [defaultDescription]);

    if (!columnsVisibility["Description"]) return null;

    if (!isReady) return <TableCell />;

    return (
        <TableCell>
            <textarea
                // sx={{ minWidth: "50ch" }}
                // multiline
                // minRows={1}
                // size="small"
                name="description"
                // label="Description"
                value={description}
                onChange={(ev) => handleChange(ev, collectee.id)}
                placeholder={"Description"}
                style={{
                    padding: "0.65rem",
                    width: "100%",
                    minWidth: "48ch",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                }}
            />
        </TableCell>
    );
}

function AdjustmentCell({
    collectee,
    rowIndex,
    onChange,
    isReady,
    defaultAdjustment,
    errorAdjustmentMessage,
}: CommonCellProps & {
    defaultAdjustment?: number;
    errorAdjustmentMessage: string | null;
}) {
    const [adjustment, setAdjustment] = useState(0);
    const debounce = useDebounce();
    const { adjustmentInputsRef, columnsVisibility, collectionsRef } =
        useContext(CollectBillsCtx);

    function handleChange(ev: ChangeEvent<any>, id: number) {
        const { value } = ev.target;

        setAdjustment(value);
        onChange(ev, id);
    }

    useEffect(() => {
        if (!defaultAdjustment) return;

        debounce(() => {
            setAdjustment(defaultAdjustment);
        }, 500);
    }, [defaultAdjustment]);

    if (!columnsVisibility["Adjustment"]) return null;

    if (!isReady) return <TableCell />;

    return (
        <MultiPermissionAuthorize ops={["CREATE READING_ADJUSTMENT"]}>
            <TableCell>
                <input
                    // size="small"
                    name="adjustment"
                    placeholder="Adjustment"
                    // label="Adjustment"
                    value={adjustment ? adjustment : ""}
                    onChange={(ev) => handleChange(ev, collectee.id)}
                    type="number"
                    min={0.0}
                    step={0.01}
                    style={{
                        padding: "0.65rem",
                        width: "100%",
                        minWidth: "15ch",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                    }}
                    ref={(el) => {
                        if (el) {
                            adjustmentInputsRef.current[rowIndex] = el;
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            if (adjustmentInputsRef.current[rowIndex + 1]) {
                                e.preventDefault();
                                adjustmentInputsRef.current[
                                    rowIndex + 1
                                ].focus();
                            }
                        }
                    }}
                />

                {errorAdjustmentMessage && (
                    <span style={{ color: "red", fontSize: "0.8rem" }}>
                        {errorAdjustmentMessage}
                    </span>
                )}
            </TableCell>
        </MultiPermissionAuthorize>
    );
}

function AdvanceCell({
    collectee,
    rowIndex,
    onChange,
    isReady,
    defaultAdvance,
    errorAdvanceMessage,
}: CommonCellProps & {
    defaultAdvance?: number;
    errorAdvanceMessage: string | null;
}) {
    const { advanceInputsRef, columnsVisibility } = useContext(CollectBillsCtx);

    if (!columnsVisibility["Advance"]) return null;
    if (!isReady) return <TableCell />;

    return (
        <TableCell>
            <input
                name="advance"
                placeholder="Advance"
                value={defaultAdvance || 0} // 🔥 single source of truth
                onChange={(ev) => onChange(ev, collectee.id)}
                type="number"
                disabled // system controlled
                style={{
                    padding: "0.65rem",
                    width: "100%",
                    minWidth: "15ch",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    background: "#f5f5f5",
                }}
                ref={(el) => {
                    if (el) {
                        advanceInputsRef.current[rowIndex] = el;
                    }
                }}
            />

            {errorAdvanceMessage && (
                <span style={{ color: "red", fontSize: "0.8rem" }}>
                    {errorAdvanceMessage}
                </span>
            )}
        </TableCell>
    );
}
